import DOMPurify from 'dompurify';
import { Marked, type MarkedExtension } from 'marked';
import type { NxMention } from '../Mentions/types';

export interface NxSanitizeOptions {
  /**
   * Keep inline `style` attributes (mail bodies). Defaults to `false`. Even
   * then, declarations that could lift content out of its box or load
   * something (`position`, `z-index`, `url(…)`, …) are dropped, and `<style>`
   * elements always go — they would restyle the whole app. Render full mail
   * HTML in a sandboxed iframe instead.
   */
  allowStyles?: boolean;
  /** Extra attributes to keep beyond DOMPurify's safe list. */
  addAttributes?: readonly string[];
  /** Elements to drop although DOMPurify would keep them (`img` for text-only previews). */
  forbidTags?: readonly string[];
}

export interface NxMarkdownOptions {
  /** Render a single line without the wrapping `<p>` (titles, table cells). */
  inline?: boolean;
  /** Single line breaks become `<br>`, as people type in chat. Defaults to `true`. */
  breaks?: boolean;
  /** Open links to other origins in a new tab (`rel="noopener noreferrer"`). Defaults to `true`. */
  externalLinksInNewTab?: boolean;
  /**
   * marked extensions and renderer overrides — wiki links, math, syntax
   * highlighting stay with the caller. Their output is sanitized like the rest.
   */
  extensions?: readonly NxMarkdownExtension[];
  sanitize?: NxSanitizeOptions;
  /**
   * People mentioned in the text, as character ranges into the source (the
   * same `NxMention` the comments use). Each range renders as a mention; the
   * text is never searched for them.
   */
  mentions?: readonly NxMention[];
  /**
   * Also read `@First Last` in the text as a mention (a name of two words, not
   * the `@` of an e-mail address) — for text that carries no ranges, as chat
   * messages do.
   */
  textMentions?: boolean;
  /** Mentions render as buttons (`<button>`) instead of plain spans. */
  mentionsInteractive?: boolean;
  /**
   * `++text++` is underlined text (`<u>`), Markdown inside it works as
   * everywhere. An opening `++` must be followed by a character that is no
   * white space and no `+`, and must not follow a letter, a digit, a `+` or a
   * backslash, so `C++ und C++` stays text; `\+\+` writes the marks. Code
   * spans and blocks are left alone, and so are link destinations,
   * autolinks, HTML tags and bare addresses: a `++` in there never closes
   * the underline.
   */
  underline?: boolean;
  /**
   * Maps the source of every image to what the app loaded for it, after the
   * HTML is sanitized (see `NxMarkdownImageSource`). Covers `img` and SVG
   * `image`; the other attributes that make the browser load a picture
   * (`srcset`, `poster`, `background`, `input[type=image]`, `feImage`,
   * `mglyph`) are removed. Unset: images keep their source and the browser
   * loads them.
   */
  imageSource?: NxMarkdownImageSource;
}

/**
 * Decides what an image of the text shows, given its source URL and alt text:
 * - a string is the source to show instead — an object URL (`blob:`) the app
 *   loaded with its own credentials, or another `http(s)` / `data:image/` URL;
 *   anything else counts as `null`;
 * - `null` shows the image not at all: its alt text stands in, as a link to
 *   the original address (plain text when that is no `http(s)` address, and
 *   inside a link the alt text only);
 * - `undefined` leaves the image as it is.
 *
 * It runs on every render of the text, so a hook that reads reactive state
 * (the objects the app has loaded so far) re-renders the text when that
 * changes — answer `null` until the image has arrived.
 */
export type NxMarkdownImageSource = (url: string, alt: string) => string | null | undefined;

/** A mention as `NxMarkdown` reports a click on it. */
export interface NxMarkdownMention {
  /** The shown text, `@Anna Demo`. */
  label: string;
  /** The person's name; for a range the `NxMention.name`, for a text mention the label without `@`. */
  name: string;
  /** The account of a range; `null` for a mention read from the text. */
  accountId: number | null;
}

function purify(html: string, options: NxSanitizeOptions): DocumentFragment {
  const forbidTags = [...(options.forbidTags ?? []), 'style'];
  const forbidAttr: string[] = options.allowStyles ? [] : ['style'];
  const fragment = DOMPurify.sanitize(html, {
    ADD_ATTR: ['target', ...(options.addAttributes ?? [])],
    FORBID_TAGS: forbidTags,
    FORBID_ATTR: forbidAttr,
    RETURN_DOM_FRAGMENT: true,
  });
  // A kept `target` must not hand the opener to the new page.
  for (const a of fragment.querySelectorAll('a[target]')) {
    a.setAttribute('rel', 'noopener noreferrer');
  }
  if (options.allowStyles) {
    for (const el of fragment.querySelectorAll<HTMLElement>('[style]')) tameStyle(el);
  }
  return fragment;
}

/**
 * Declarations foreign content must not carry even when styles are kept: they
 * let it cover the app (a fake login over the real one) or fetch from outside.
 */
const UNSAFE_STYLE_PROPERTY =
  /^(position|z-index|inset|top|right|bottom|left|translate|transform|zoom|behavior|-moz-binding|content|cursor|pointer-events|filter|backdrop-filter|mask.*|clip-path)$/i;
const UNSAFE_STYLE_VALUE = /url\s*\(|image-set\s*\(|expression\s*\(|javascript:|var\s*\(/i;

function tameStyle(el: HTMLElement): void {
  const style = el.style;
  for (const property of Array.from(style)) {
    const value = style.getPropertyValue(property);
    if (UNSAFE_STYLE_PROPERTY.test(property) || UNSAFE_STYLE_VALUE.test(value)) {
      style.removeProperty(property);
    }
  }
  if (!style.length) el.removeAttribute('style');
}

function serialize(fragment: DocumentFragment): string {
  const box = document.createElement('div');
  box.append(fragment);
  return box.innerHTML;
}

/**
 * HTML made safe to render with `v-html` / `innerHTML`: scripts, event
 * handlers and `javascript:` URLs go, `style` goes unless `allowStyles`, and
 * every link that opens a new tab gets `rel="noopener noreferrer"`. Needs a
 * DOM (browser or jsdom).
 */
export function sanitizeHtml(html: string, options: NxSanitizeOptions = {}): string {
  if (!html) return '';
  return serialize(purify(html, options));
}

function isExternal(href: string): boolean {
  if (!/^[a-z][a-z\d+.-]*:/i.test(href) && !href.startsWith('//')) return false;
  try {
    const url = new URL(href, window.location.href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    return url.origin !== window.location.origin;
  } catch {
    return false;
  }
}

// A range mention is spliced into the source as `OPEN index CLOSE` before the
// parse, so Markdown around it (a `*` pair, a list marker) cannot break it; the
// inline tokenizer below turns the marker into the mention. Both characters sit
// in the Unicode private-use area and are stripped from the text first.
const MENTION_OPEN = '\uE000';
const MENTION_CLOSE = '\uE001';
const MENTION_MARKER = new RegExp(`${MENTION_OPEN}(\\d+)${MENTION_CLOSE}`, 'g');
const MENTION_MARKER_START = new RegExp(`^${MENTION_OPEN}(\\d+)${MENTION_CLOSE}`);
// `@First Last`: a text mention names an account by two words. `@channel`
// and `@here` name a whole conversation (chat, I-3826) and are one word: the
// word after them is no part of the mention.
const TEXT_MENTION =
  /^@(?:(?:channel|here)(?![\p{L}\p{N}_@-])|[A-Za-z\u00C0-\u024F]+ [A-Za-z\u00C0-\u024F]+)/u;

function escapeText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * The source with its mention ranges replaced by markers. Ranges that do not
 * fit (out of bounds, overlapping, empty) are skipped and stay plain text, like
 * `renderMentionText` does.
 */
function spliceMentions(source: string, mentions: readonly NxMention[]): string {
  const strip = (text: string) => text.replace(/[\uE000\uE001]/g, '');
  const ordered = mentions
    .map((mention, index) => ({ mention, index }))
    .sort((a, b) => a.mention.start - b.mention.start);
  let out = '';
  let cursor = 0;
  for (const { mention, index } of ordered) {
    if (mention.start < cursor || mention.end > source.length || mention.end <= mention.start) {
      continue;
    }
    out += strip(source.slice(cursor, mention.start)) + MENTION_OPEN + index + MENTION_CLOSE;
    cursor = mention.end;
  }
  return out + strip(source.slice(cursor));
}

/** A marker whose mention ended up where no inline token reads it (a code span): the text as written. */
function restoreMentionMarkers(
  html: string,
  source: string,
  mentions: readonly NxMention[]
): string {
  return html.replace(MENTION_MARKER, (_marker, index: string) => {
    const mention = mentions[Number(index)];
    return mention ? escapeText(source.slice(mention.start, mention.end)) : '';
  });
}

function mentionHtml(
  label: string,
  name: string,
  accountId: number | null,
  interactive: boolean
): string {
  const attrs = ` class="nx-markdown__mention" data-mention-name="${escapeText(name)}"${
    accountId === null ? '' : ` data-mention-account="${accountId}"`
  } title="${escapeText(name)}"`;
  return interactive
    ? `<button type="button"${attrs}>${escapeText(label)}</button>`
    : `<span${attrs}>${escapeText(label)}</span>`;
}

/** The marked extension behind the mention options; `undefined` when there are none. */
function mentionExtension(options: NxMarkdownOptions): MarkedExtension | undefined {
  const mentions = options.mentions ?? [];
  const interactive = options.mentionsInteractive ?? false;
  const extensions: NonNullable<MarkedExtension['extensions']> = [];
  if (mentions.length) {
    extensions.push({
      name: 'nxMentionRange',
      level: 'inline',
      start: (src: string) => {
        const at = src.indexOf(MENTION_OPEN);
        return at < 0 ? undefined : at;
      },
      tokenizer(src: string) {
        const match = MENTION_MARKER_START.exec(src);
        return match ? { type: 'nxMentionRange', raw: match[0], text: match[1] } : undefined;
      },
      renderer(token) {
        const mention = mentions[Number(token.text)];
        return mention
          ? mentionHtml(mention.label, mention.name, mention.accountId, interactive)
          : '';
      },
    });
  }
  if (options.textMentions) {
    extensions.push({
      name: 'nxMentionText',
      level: 'inline',
      // Not the `@` of an e-mail address: the autolink wants those.
      start: (src: string) => {
        const at = src.search(/(?<![\w.%+-])@/);
        return at < 0 ? undefined : at;
      },
      tokenizer(src: string) {
        const match = TEXT_MENTION.exec(src);
        return match ? { type: 'nxMentionText', raw: match[0], text: match[0] } : undefined;
      },
      renderer: (token) =>
        mentionHtml(String(token.text), String(token.text).slice(1), null, interactive),
    });
  }
  return extensions.length ? { extensions } : undefined;
}

// `++text++`: the opening marks are followed by a character that is neither
// white space nor a `+`, the closing ones are preceded by such a character and
// not followed by a `+`. The text between holds no other `++`, and the closing
// marks are looked for outside code spans, link destinations, autolinks, HTML
// tags and escapes (`underlineClose`).
const UNDERLINE_OPEN = /^\+\+(?=[^\s+])/;
const UNDERLINE_ANGLE =
  /^<(?:[a-zA-Z][a-zA-Z\d+.-]{1,31}:[^\s<>]*|[^\s@<>]+@[^\s<>]+|\/?[a-zA-Z][^<>]*)>/;
const UNDERLINE_URL = /^(?:https?:\/\/|www\.)[^\s<]*/i;

/** Behind the code span that starts at `at`; `at` + the backtick run when it never closes. */
function skipCodeSpan(src: string, at: number): number {
  let run = at;
  while (src[run] === '`') run++;
  const ticks = run - at;
  for (let i = run; i < src.length; i++) {
    if (src[i] !== '`') continue;
    let end = i;
    while (src[end] === '`') end++;
    if (end - i === ticks) return end;
    i = end - 1;
  }
  return run;
}

/** Behind the `(…)` destination that starts at `at`; `at` + 1 when it never closes. */
function skipDestination(src: string, at: number): number {
  if (src[at + 1] === '<') {
    const end = src.indexOf('>', at + 2);
    return end < 0 ? at + 1 : end + 1;
  }
  let depth = 0;
  for (let i = at; i < src.length; i++) {
    if (src[i] === '\\') i++;
    else if (src[i] === '(') depth++;
    else if (src[i] === ')' && --depth === 0) return i + 1;
  }
  return at + 1;
}

/**
 * Where the closing `++` of an underline that opens at the start of `src`
 * begins, or -1. A `++` inside a code span, a link destination (`](…)`), an
 * autolink or HTML tag, a bare address or behind a backslash is text, so it
 * cannot end the underline. Any other `++` that is no closing mark (after
 * white space, or a longer run of `+`) means there is no underline.
 */
function underlineClose(src: string): number {
  // The unit before `i` is a literal (escape, code, link, …): a good place to close behind.
  let literal = false;
  let i = 2;
  while (i < src.length) {
    const ch = src[i];
    let next = i;
    if (ch === '\\') next = Math.min(i + 2, src.length);
    else if (ch === '`') next = skipCodeSpan(src, i);
    else if (ch === ']' && src[i + 1] === '(') next = skipDestination(src, i + 1);
    else if (ch === '<') next = i + (UNDERLINE_ANGLE.exec(src.slice(i))?.[0].length ?? 0);
    else if (
      (ch === 'h' || ch === 'H' || ch === 'w' || ch === 'W') &&
      !/[\p{L}\p{N}]/u.test(src[i - 1])
    ) {
      const url = UNDERLINE_URL.exec(src.slice(i))?.[0] ?? '';
      // An address that ends in `++` hands those marks back as the closing ones.
      next = i + (/(?<=[^\s+])\+\+$/.test(url) ? url.length - 2 : url.length);
    }
    if (next > i) {
      literal = true;
      i = next;
      continue;
    }
    if (ch !== '+') {
      literal = false;
      i++;
      continue;
    }
    let run = i;
    while (src[run] === '+') run++;
    if (run - i >= 2) {
      return run - i === 2 && (literal || /[^\s+]/.test(src[i - 1])) ? i : -1;
    }
    literal = false;
    i = run;
  }
  return -1;
}

// What an opening `++` must not follow: `C++`, `1+++2`, `\++`.
const NOT_BEFORE_UNDERLINE = /[\p{L}\p{N}+\\]/u;

/**
 * Where an underline may open in `src`. A `++` at the very start of `src`
 * cannot be judged here (marked hands the text on without its predecessor), so
 * it stays a candidate; the tokenizer looks at the text before it.
 */
function underlineStart(src: string): number | undefined {
  for (const match of src.matchAll(/\+\+/g)) {
    const at = match.index ?? 0;
    if (at === 0 || !NOT_BEFORE_UNDERLINE.test(src[at - 1])) return at;
  }
  return undefined;
}

/** The marked extension behind the `underline` option. */
function underlineExtension(): MarkedExtension {
  return {
    extensions: [
      {
        name: 'nxUnderline',
        level: 'inline',
        start: underlineStart,
        tokenizer(src: string, tokens: readonly { raw?: string }[]) {
          if (!UNDERLINE_OPEN.test(src)) return undefined;
          const close = underlineClose(src);
          if (close < 0) return undefined;
          const before = tokens.at(-1)?.raw?.slice(-1) ?? '';
          if (before && NOT_BEFORE_UNDERLINE.test(before)) return undefined;
          const text = src.slice(2, close);
          return {
            type: 'nxUnderline',
            raw: src.slice(0, close + 2),
            text,
            tokens: this.lexer.inlineTokens(text),
          };
        },
        renderer(token) {
          return `<u>${this.parser.parseInline(token.tokens ?? [])}</u>`;
        },
      },
    ],
  };
}

/** Builds a marked instance for the options; reuse it when the options stay put. */
export function createMarkdownParser(options: NxMarkdownOptions = {}): Marked {
  const parser = new Marked({ gfm: true, breaks: options.breaks ?? true });
  const mentionExt = mentionExtension(options);
  if (mentionExt) parser.use(mentionExt);
  if (options.underline) parser.use(underlineExtension());
  for (const extension of options.extensions ?? []) parser.use(extension);
  return parser;
}

// What the app may hand back as an image source: its own object URL, a web
// address, a raster data URL. Not SVG, not `javascript:`.
const SHOWN_IMAGE_SOURCE = /^(blob:|https?:\/\/|data:image\/(png|jpe?g|gif|webp|avif);)/i;

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

/** What the hook answers for one image; a hook that throws hides the image. */
function askImageSource(
  hook: NxMarkdownImageSource,
  url: string,
  alt: string
): string | null | undefined {
  try {
    return hook(url, alt);
  } catch {
    // A broken hook must not blank the text: the image is not shown.
    return null;
  }
}

/** True for an answer of the hook that may be loaded. */
function isShownSource(source: string | null | undefined): source is string {
  return typeof source === 'string' && SHOWN_IMAGE_SOURCE.test(source);
}

/**
 * Runs the app's image hook over the sanitized fragment. The source it hands
 * back is set after the sanitizer ran, which is why `blob:` needs no allowance
 * there — and why only the accepted schemes get through.
 *
 * The sanitizer lets more than `img[src]` through that makes the browser load
 * a picture, and each of them would bypass the hook: `srcset` (the browser
 * prefers it over `src`), SVG `image` and `feImage`, MathML `mglyph`,
 * `video[poster]`, `input[type=image]` and the `background` attribute. SVG
 * `image` goes through the hook like `img`; the others are removed.
 */
function mapImageSources(fragment: DocumentFragment, hook: NxMarkdownImageSource): void {
  for (const el of fragment.querySelectorAll('[srcset]')) el.removeAttribute('srcset');
  for (const el of fragment.querySelectorAll('[poster], [background]')) {
    el.removeAttribute('poster');
    el.removeAttribute('background');
  }
  for (const el of fragment.querySelectorAll('input[src], svg *, mglyph')) {
    if (el.localName === 'input') el.removeAttribute('src');
    // Selectors fold the case of `feImage` differently per engine: compare by hand.
    else if (el.localName.toLowerCase() === 'feimage' || el.localName === 'mglyph') el.remove();
  }
  for (const image of fragment.querySelectorAll('image')) {
    if (image.namespaceURI !== SVG_NAMESPACE) continue;
    const url = image.getAttribute('href') ?? image.getAttribute('xlink:href') ?? '';
    const source = askImageSource(hook, url, '');
    if (source === undefined) continue;
    if (isShownSource(source)) {
      image.removeAttribute('xlink:href');
      image.setAttribute('href', source);
    } else {
      image.remove();
    }
  }
  for (const img of fragment.querySelectorAll('img')) {
    const url = img.getAttribute('src') ?? '';
    const alt = img.getAttribute('alt') ?? '';
    const source = askImageSource(hook, url, alt);
    if (source === undefined) continue;
    if (isShownSource(source)) {
      img.setAttribute('src', source);
      continue;
    }
    const label = alt || url;
    const insideLink = img.closest('a') !== null;
    if (insideLink || !/^https?:\/\//i.test(url)) {
      img.replaceWith(document.createTextNode(label));
      continue;
    }
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.textContent = label;
    img.replaceWith(link);
  }
}

/**
 * Markdown to sanitized HTML (GitHub flavour: tables, task lists,
 * strikethrough). Raw HTML in the source is sanitized, not escaped — pass a
 * source through `escapeHtml` first where people must not write HTML at all.
 */
export function renderMarkdown(
  source: string,
  options: NxMarkdownOptions = {},
  parser: Marked = createMarkdownParser(options)
): string {
  if (!source) return '';
  const mentions = options.mentions ?? [];
  const parsed = mentions.length ? spliceMentions(source, mentions) : source;
  let html: string;
  try {
    html = (
      options.inline
        ? parser.parseInline(parsed, { async: false })
        : parser.parse(parsed, { async: false })
    ) as string;
    if (mentions.length) html = restoreMentionMarkers(html, source, mentions);
  } catch {
    // A broken extension must not blank the page: show the text as written.
    const pre = document.createElement('pre');
    pre.textContent = source;
    return pre.outerHTML;
  }
  const fragment = purify(html, options.sanitize ?? {});
  if (options.imageSource) mapImageSources(fragment, options.imageSource);
  if (options.externalLinksInNewTab !== false) {
    for (const a of fragment.querySelectorAll('a[href]')) {
      if (!isExternal(a.getAttribute('href') ?? '')) continue;
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    }
  }
  return serialize(fragment);
}

/** A marked extension or renderer override, as `NxMarkdown` takes it. */
export type NxMarkdownExtension = MarkedExtension;
